import { AppChipColors } from "../../../../components/dataDisplay/chips/appChip"
import { NETWORK_TO_DNO_FRIENDLY_ABBREVIATION_DISPATCHER } from "../../../../constants/dnos"
import {
    ApplicationClasses,
    ApplicationTag,
    ExistingDeviceClasses,
    LCTOnlyDatasets,
    ProposedDeviceClasses,
} from "../../../../constants/enums"
import { NA } from "../../../../constants/generalConstants"
import {
    formatDate,
    getValue,
    objectOnCondition,
} from "../../../../utils/general/generalUtils"
import {
    IApplicationComment,
    TApplicationCommissioningInformation,
    TDisplayActionHistory,
} from "../../types/installerInterface/installerApplicationTypes"

export interface IApplicationSummary {
    applicationId: string
    applicationStatus: string
    submittedAgo: string
    submittedAgoRag: AppChipColors
    applicationDate: string | null | undefined
    postCode: string
    address: string
    installerCompany: string
    lcts: string[]
    totAggGen: string
    maxDemand: string
    isRedacted: boolean | undefined
    assignedTo: string | undefined
    lastUpdateDate: string
    duplicateApplicationIds?: string[]
    isDNOThreadUnread?: boolean
}

export type TSummaryFacts = {
    "Created Date": string
    "Post Code": string
    "Installer Company": string
    "Installer Contact Email": string
    "DNO Imposed Limit": string | undefined
    "DNO Response Comments": string | undefined
    "Assessed By": string | undefined
}
export type TAttachment = {
    fileUrl: string
    fileName?: string
}

export type ExistingDevice = Record<string, string | number>
export type TDevicesToInstall = Record<string, string | number | boolean>

export interface ISingleApplicationDetail {
    address: string
    applicationClass: ApplicationClasses
    applicationStatus: string
    customerInstallerDetails: Record<string, string | number>
    cutOutImages: TAttachment[]
    additionalAttachments: TAttachment[]
    dno: string
    createdDate: string
    devicesToInstall: TDevicesToInstall[]
    enaAssessment: Record<string, boolean | string>
    existingDevices: ExistingDevice[]
    lcts: string[]
    summaryFacts: TSummaryFacts
    supplyDetails: Record<string, string | number>
    totAggGen: string
    maxDemand: string
    isRedacted: boolean | undefined
    assignedTo: string | undefined
    comments?: IApplicationComment[]
    isResubmit?: boolean
    tag?: ApplicationTag
    commissioningInfo?: TApplicationCommissioningInformation
    actionHistoryToDisplay: TDisplayActionHistory[]
    duplicateApplicationIds?: string[]
    isDNOThreadUnread?: boolean
    lastReadByDnoUsername?: string
    lastReadByDnoTimestamp?: string
}

type TApplicationSizes = {
    totAggGen: string
    maxDemand: string
}
/**
 * Returns the total aggregate generation capacity and max demand for an application
 * @param {Record<string, unknown>} res - application data from API
 * @returns - total aggregate generation capacity and max demand
 */
const getApplicationSizes = (
    res: Record<string, unknown>
): TApplicationSizes => ({
    totAggGen:
        (
            res?.newPremisesGenerationCapacityKw as unknown as Record<
                string,
                string
            >
        )?.newPremisesGenerationCapacityKw || NA,
    maxDemand:
        (res?.newPremisesMaxDemand as unknown as Record<string, string>)
            ?.newPremisesMaxDemand || NA,
})

/**
 * Checks if devices to install are type safe
 * @param devicesToInstall - devices to install input from API
 * @returns {boolean} - if API response is type safe
 */
function isValidDevicesToInstall(
    devicesToInstall: unknown
): devicesToInstall is TDevicesToInstall[] {
    const isValid = (devicesToInstall as TDevicesToInstall[]).every(
        (device) => {
            // Confirm all devices have a device class
            if (device?.deviceClass === undefined) {
                return false
            }

            // If the device is tagged as generation, or HP in database confirm
            // it has a database ID
            if (
                device.deviceClass === ProposedDeviceClasses.genGeneral ||
                device.deviceClass === ProposedDeviceClasses.hpRegistered ||
                device.deviceClass ===
                    ProposedDeviceClasses.demandGeneralRegistered
            ) {
                if (device?.deviceSysRef === undefined) {
                    return false
                }
            }

            return true
        }
    )
    if (!isValid) {
        throw Error()
    }
    return isValid
}

/**
 * Checks if application class is valid
 * @param applicationClass - application class input from API
 * @returns {boolean} - if API response is type safe
 */
function isApplicationClass(
    applicationClass: unknown
): applicationClass is ApplicationClasses {
    return Object.values(ApplicationClasses).includes(
        applicationClass as ApplicationClasses
    )
}

/**
 * Confirms if existing devices sent from API are valid and type safe.
 * @param existingDevices - existing devices input from API
 * @returns {boolean} - if API response is type safe
 */
function isValidExistingDevices(
    existingDevices: unknown
): existingDevices is ExistingDevice[] {
    const isValid = (existingDevices as ExistingDevice[]).every((device) => {
        // Confirm all devices have a device class
        if (device?.deviceClass === undefined) {
            return false
        }

        // If the device is tagged as existing generation confirm it has a database ID
        if (
            device.deviceClass === ExistingDeviceClasses.existingGenRegistered
        ) {
            if (device?.deviceSysRef === undefined) {
                return false
            }
        }

        // If the device is tagged as existing demand confirm it has a database ID
        // and has a valid LCT dataset tag
        if (
            device.deviceClass ===
            ExistingDeviceClasses.existingDemandRegistered
        ) {
            const isIdMissing = device?.deviceSysRef === undefined

            const isDatasetInvalid = ![
                LCTOnlyDatasets.EVAC,
                LCTOnlyDatasets.EVDC,
                LCTOnlyDatasets.HP,
            ].includes(device.enaLctDataset as LCTOnlyDatasets)

            if (isIdMissing || isDatasetInvalid) {
                return false
            }
        }
        return true
    })
    if (!isValid) {
        throw Error()
    }
    return isValid
}

// Assumed raw input type
interface IRawSearchResult {
    Count: number
    LastEvaluatedKey: Record<string, string>
    Items: Record<string, unknown>[]
    ScannedCount: number
}

// Required output type
export interface IApplications {
    totalResults: number
    pageSize: number
    data: IApplicationSummary[]
    downloadData: unknown[]
    nextPageKey: Record<string, string>
}

export const dnoApplicationsMapper = {
    /**
     * Map `getDnoApplication` into UI data model, confirming validity of data
     * in process. Formats data for use in UI and provides unformatted version
     * for download. Unformatted version should not be used in any UI components
     *
     * Some fields are deemed critical and their omission will trigger an
     * API error. Others will not trigger an API error, and will be replaced
     * with a default value.
     *
     * Note ev ac dataset does not have a type. As such, this field is hard coded
     * @param {unknown} response - axios client response
     * @returns {IApplicationSummary[]} - axios client response parsed into
     * UI data model format
     */
    mapList: (response: unknown): IApplications => {
        // Confirm response.Items is array
        const resTypeGuarded = response as IRawSearchResult
        if (!Array.isArray(resTypeGuarded.Items)) {
            throw Error()
        }
        // Parse response.data into UI data model
        const resultsFormatted = (
            resTypeGuarded.Items as Record<string, string>[]
        ).map((result): IApplicationSummary => {
            const installerCustDetails =
                result?.installerCustomerDetails as unknown as Record<
                    string,
                    string
                >
            return {
                applicationId: result.applicationId,
                applicationStatus: result?.applicationStatus || "Data error",
                submittedAgo: result?.timeAgo,
                applicationDate: formatDate(result?.createdDate),
                installerCompany:
                    installerCustDetails?.installerCompany || "Not available",
                postCode:
                    installerCustDetails?.installationPostCode ||
                    "Not available",
                address: installerCustDetails?.mainAddress || "Not available",
                lcts: (result.lcts as unknown as string[]) || [
                    "LCT type not available",
                ],
                submittedAgoRag: (result?.rag || undefined) as AppChipColors,
                ...getApplicationSizes(result),
                isRedacted: result?.isRedacted as unknown as
                    | boolean
                    | undefined,
                assignedTo: result?.assignedTo as string | undefined,
                duplicateApplicationIds: Array.isArray(
                    result?.duplicateApplicationIds
                )
                    ? (result?.duplicateApplicationIds as string[])
                    : undefined,
                lastUpdateDate:
                    typeof result?.modifiedEpochTime === "number"
                        ? formatDate(
                              new Date(
                                  (result?.modifiedEpochTime as number) * 1000
                              )
                          )
                        : "Not Found",
                isDNOThreadUnread:
                    result?.isDNOThreadUnread as unknown as boolean,
            }
        })
        return {
            totalResults: resTypeGuarded.Count,
            pageSize: resTypeGuarded.ScannedCount,
            data: resultsFormatted,
            downloadData: resTypeGuarded.Items, // For download
            nextPageKey: resTypeGuarded.LastEvaluatedKey,
        }
    },

    /**
     * Crates UI data model for application detail page.
     * @param {unknown} response axios client response
     * @returns {ISingleApplicationDetail | undefined} - application detail
     * payload, or undefined if no data returned
     */
    mapSingle: (response: unknown): ISingleApplicationDetail | undefined => {
        // Confirm response.Items is array
        const resTypeGuarded = response as IRawSearchResult
        if (!Array.isArray(resTypeGuarded.Items)) {
            throw Error()
        }

        // Confirm array has length > 0
        if (resTypeGuarded.Items.length === 0) {
            return undefined
        }

        // Extract single res
        const singleRes = resTypeGuarded.Items[0]

        // Type check application class
        let appClass: ApplicationClasses

        if (isApplicationClass(singleRes?.applicationClass)) {
            appClass = singleRes?.applicationClass
        } else {
            throw Error()
        }

        // Extract limits
        const dnoResponse = singleRes?.dnoApplicationResponse as Record<
            string,
            string
        >

        const installerCustDetails =
            singleRes?.installerCustomerDetails as unknown as Record<
                string,
                string
            >

        return {
            applicationStatus:
                (singleRes?.applicationStatus as string) ?? "Data error",
            applicationClass: appClass,
            address:
                installerCustDetails?.mainAddress || "Address Not available",
            createdDate: formatDate(singleRes?.createdDate),
            lcts: (singleRes.lcts as unknown as string[]) ?? [
                "LCT type not available",
            ],
            dno:
                getValue(
                    NETWORK_TO_DNO_FRIENDLY_ABBREVIATION_DISPATCHER,
                    singleRes.dno as string
                ) ?? (singleRes.dno as string),
            summaryFacts: {
                ...objectOnCondition(!!dnoResponse?.responderName, {
                    "Assessed By": dnoResponse?.responderName,
                }),
                "Created Date": formatDate(singleRes?.createdDate),
                "Post Code":
                    (
                        singleRes?.installerCustomerDetails as Record<
                            string,
                            string | number
                        >
                    )?.installationPostCode || "Post code not available",
                "Installer Company":
                    (
                        singleRes?.installerCustomerDetails as Record<
                            string,
                            string | number
                        >
                    )?.installerCompany || "Installer company not available",
                "Installer Contact Email":
                    (
                        singleRes?.installerCustomerDetails as Record<
                            string,
                            string | number
                        >
                    )?.installerEmail || "Installer email name not available",
                ...objectOnCondition(!!singleRes?.comment, {
                    "DNO Response Comments": singleRes?.comment,
                }),
            } as TSummaryFacts,
            customerInstallerDetails:
                singleRes?.installerCustomerDetails as Record<
                    string,
                    string | number
                >,
            existingDevices: isValidExistingDevices(singleRes?.existingDevices)
                ? singleRes?.existingDevices
                : [],
            devicesToInstall: isValidDevicesToInstall(
                singleRes?.devicesToInstall
            )
                ? singleRes?.devicesToInstall
                : [],
            enaAssessment: (singleRes?.enaAssessment as Record<
                string,
                boolean | string
            >) || {
                enaPlatformDecision: "Error retrieving data",
                "No assessment found - please refresh": false,
            },
            supplyDetails: Object.keys(singleRes.supplyDetails || {}).reduce(
                (acc, key) => {
                    const rawValue = (
                        singleRes.supplyDetails as Record<string, unknown>
                    )[key]
                    return { ...acc, [key]: rawValue }
                },
                {}
            ),
            cutOutImages: (singleRes?.cutOutImages as TAttachment[]) || [],
            ...getApplicationSizes(singleRes),
            additionalAttachments:
                (singleRes?.additionalAttachments as TAttachment[]) || [],
            isRedacted: singleRes?.isRedacted as boolean,
            isResubmit: singleRes?.isResubmit as boolean,
            assignedTo: singleRes?.assignedTo as string,
            comments: (singleRes?.comments as IApplicationComment[]) || [],
            tag: singleRes?.tag as ApplicationTag,
            actionHistoryToDisplay:
                (singleRes?.actionHistoryToDisplay as TDisplayActionHistory[]) ||
                [],
            commissioningInfo:
                singleRes?.commissioningInfo as TApplicationCommissioningInformation,
            duplicateApplicationIds: Array.isArray(
                singleRes?.duplicateApplicationIds
            )
                ? (singleRes?.duplicateApplicationIds as string[])
                : undefined,
            isDNOThreadUnread:
                singleRes?.isDNOThreadUnread as unknown as boolean,
            lastReadByDnoUsername:
                singleRes?.lastReadByDnoUsername as unknown as string,
            lastReadByDnoTimestamp:
                singleRes?.lastReadByDnoTimestamp as unknown as string,
        }
    },

    /**
     * Maps single application for download. This extracts the raw response
     * from the API and returns it. This is used for downloading the application
     * @param response - axios client response
     * @returns {unknown} - raw response from API to be passed to download
     */
    mapSingleForDownload: (response: unknown): unknown => {
        // Confirm response.Items is array
        const resTypeGuarded = response as IRawSearchResult
        if (!Array.isArray(resTypeGuarded.Items)) {
            throw Error()
        }

        // Confirm array has length > 0
        if (resTypeGuarded.Items.length === 0) {
            return undefined
        }

        return resTypeGuarded.Items[0]
    },

    mergePaginatedData: (
        data: IApplications | undefined
    ): IApplications | undefined => data,
}
